import { logger } from "@/modules/logger";
import { AudioRecorderLockingError } from "@/domains/errors/kinds/contention";

const AUDIO_RECORDER_LOCK_IDENTIFIER = "audio-recorder-lock-v2";

export interface AudioRecorderLockHandle {
  release: () => Promise<void>;
}

export async function takeAudioRecorderLock(): Promise<AudioRecorderLockHandle> {
  return new Promise<AudioRecorderLockHandle>((resolve, reject) => {
    navigator.locks
      .request(
        AUDIO_RECORDER_LOCK_IDENTIFIER,
        { mode: "exclusive", ifAvailable: true },
        async lock => {
          if (!lock) {
            throw new AudioRecorderLockingError();
          }

          logger.debug({
            message: "[AppStoreAudioCaptureStore] acquired audio recorder lock",
          });

          let releaseResolver: (() => void) | null = null;

          const waitUntilRelease = new Promise<void>(resolveRelease => {
            releaseResolver = resolveRelease;
          });

          let hasReleased = false;
          const handle: AudioRecorderLockHandle = {
            release: async () => {
              if (hasReleased) return;
              hasReleased = true;
              const toResolve = releaseResolver;
              releaseResolver = null;
              toResolve?.();
            },
          };

          resolve(handle);

          return waitUntilRelease;
        }
      )
      .catch(error => {
        reject(error);
      });
  });
}

export async function releaseAudioRecorderLock(
  handle: AudioRecorderLockHandle | null | undefined
): Promise<void> {
  if (!handle) return;

  try {
    await handle.release();
  } catch (error) {
    logger.warn({
      message: "[AppStoreAudioCaptureStore] failed to release audio recorder lock",
      info: { error: error instanceof Error ? error.message : String(error) },
    });
  }
}
